import { inject, Injectable } from '@angular/core';
import { BandState } from '../../shared/band-state/band.state';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import dayjs from 'dayjs';
import { BandStateActions } from '../../shared/band-state/band-state.actions';
import { RehearsalStatusEnum } from '../models/rehearsal-status.enum';
import {
  BandmanagerRestApiAdsTypeEnum, BandmanagerRestApiAllEventsService,
  BandmanagerRestApiEvent,
  BandmanagerRestApiMyBandService,
  BandmanagerRestApiRehearsal,
} from '@digitale-menschen/bandmanager-rest-api';
import { BehaviorSubject, lastValueFrom, Observable } from 'rxjs';
import { AdsService } from './ads.service';
import { EventsState } from '../../shared/events-state/events.state';
import { AnalyticsService } from './analytics.service';
import { SettingsState } from '../../shared/settings-state/settings-state.state';
import { environment } from '../../environments/environment';
import utc from 'dayjs/plugin/utc';
import { PushNotificationSchema } from "@capacitor/push-notifications";

dayjs.extend(utc);

@Injectable({
  providedIn: 'root',
})
export class RehearsalService {
  private static readonly DEFAULT_TIMER_INTERVAL = 10000;
  private lastRehearsal: BandmanagerRestApiRehearsal | null = null;
  private hasConflictingRehearsalEvent = new BehaviorSubject<BandmanagerRestApiEvent | null>(null);
  public hasConflictingRehearsalEvent$: Observable<BandmanagerRestApiEvent | null> = this.hasConflictingRehearsalEvent.asObservable();
  private statusUpdateTimer: any = null;
  private nextEvent: BandmanagerRestApiEvent | null = null;
  private rehearsalFinished = false;
  private readonly store = inject(Store);
  private readonly adsService = inject(AdsService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly apiMyBandService = inject(BandmanagerRestApiMyBandService);
  private readonly apiEventService = inject(BandmanagerRestApiAllEventsService);

  constructor() {
    this.observeStoreUpdates();
  }

  private observeStoreUpdates(): void {
    this.store
      .select(BandState.lastRehearsal)
      .pipe(takeUntilDestroyed())
      .subscribe((lastRehearsal) => {
        this.lastRehearsal = lastRehearsal;
        this.updateStatus();
      });

    this.store
      .select(EventsState.nextParticipations)
      .pipe(takeUntilDestroyed())
      .subscribe((nextParticipations) => {
        this.handleNextEvent(nextParticipations);
      });
  }

  public startRehearsalStatusUpdateInterval(interval: number = RehearsalService.DEFAULT_TIMER_INTERVAL): void {
    this.updateStatus();
    if (!this.statusUpdateTimer) {
      this.statusUpdateTimer = setInterval(() => {
        this.updateStatus();
      }, interval);
    }
  }

  private handleNextEvent(nextParticipations: any[]): void {
    if (nextParticipations && nextParticipations.length > 0 && nextParticipations[0].eventSection) {
      this.nextEvent = nextParticipations[0].eventSection.event;
    } else {
      this.nextEvent = null;
    }
    this.updateStatus();
  }

  public async startRehearsal(force = false): Promise<void> {
    if (!force) {
      const nextEventDuringRehearsal = await this.nextEventDuringRehearsal();
      if (nextEventDuringRehearsal) {
        this.hasConflictingRehearsalEvent.next(nextEventDuringRehearsal);
        return;
      }
    }

    this.rehearsalFinished = false;
    await this.adsService.showAds(BandmanagerRestApiAdsTypeEnum.InterstitialRehearsalStart);
    await lastValueFrom(this.apiMyBandService.bandControllerStartRehearsal());
    this.store.dispatch(
      new BandStateActions.SetRehearsal(
        await lastValueFrom(this.apiMyBandService.bandControllerGetMyLatestRehearsal()),
      ),
    );
    this.updateRehearsalState(RehearsalStatusEnum.InProgress);
    this.startRehearsalStatusUpdateInterval();
    await this.analyticsService.logEvent({name: 'rehearsal_start'});
  }

  /**
   * Check if there is a next unregistered-event which starts during the rehearsal (if we start rehearsal now)
   */
  public async nextEventDuringRehearsal(onlyRegistered = false): Promise<BandmanagerRestApiEvent | null> {
    const settingRehearsalDuration = this.store.selectSnapshot(SettingsState.settingByKey(environment.settingsKeys.rehearsalDuration)).hour;

    // take future events
    const payload: any = {
      page: 0,
      limit: 15,
      sortBy: 'startAt:ASC',
    };
    payload["filter.startAt"] = '$gte:' + dayjs().format('YYYY-MM-DDTHH:mm:ss.SSSZ');
    const results = await lastValueFrom(this.apiEventService.eventsDeliveryControllerGetAllPaginated(payload));
    if (!results || !results.data || results.data.length === 0) {
      return null;
    }

    // filter for starting not later than end of Rehearsal
    let filteredEvents = results.data.filter((event: BandmanagerRestApiEvent) =>
      dayjs(event.startAt).utc().isBefore(dayjs().utc().add(settingRehearsalDuration, "hour").add(1, "minute"))
      && dayjs(event.startAt).utc().isSame(dayjs().utc(), "day")
    );
    ;
    if (onlyRegistered) {
      filteredEvents = this.extractRegisteredEvents(filteredEvents);
    } else {
      filteredEvents = this.extractUnregisteredEvents(filteredEvents);
    }
    return filteredEvents.length > 0 ? filteredEvents[0] : null;
  }

  /**
   * Filter events and take only those where I don't participate
   * @param events
   * @private
   */
  private extractUnregisteredEvents(events: BandmanagerRestApiEvent[]): BandmanagerRestApiEvent[] {
    const myParticipations = this.store.selectSnapshot(EventsState.eventsParticipations);
    return events.filter(event => {
      return !myParticipations.find(participation => participation.eventSection.event.id === event.id);
    });
  }

  /**
   * Filter events and take only those where I participate
   * @param events
   * @private
   */
  private extractRegisteredEvents(events: BandmanagerRestApiEvent[]): BandmanagerRestApiEvent[] {
    const myParticipations = this.store.selectSnapshot(EventsState.eventsParticipations);
    return events.filter(event => {
      return myParticipations.find(participation => participation.eventSection.event.id === event.id);
    })
  }

  private updateStatus(): void {
    if (this.nextEvent && this.handleNextEventConflict()) {
      return;
    }

    if (this.lastRehearsal) {
      this.evaluateRehearsalStatus();
    } else {
      this.updateRehearsalState(RehearsalStatusEnum.Possible);
    }
  }

  private handleNextEventConflict(): boolean {
    const possibleStart = this.determineRehearsalStartTime();
    const rehearsalDurationHours = this.getRehearsalDuration();
    const nextEndOfRehearsal = dayjs(possibleStart).add(rehearsalDurationHours, 'hour');

    if (nextEndOfRehearsal.isAfter(dayjs(this.nextEvent!.startAt))) {
      // Rehearsal would overlap with the event, so it's not possible.
      this.updateRehearsalState(RehearsalStatusEnum.None);
      return true;
    }
    return false;
  }

  private determineRehearsalStartTime(): dayjs.Dayjs {
    let possibleStart = dayjs();

    if (this.lastRehearsal?.nextPossibleRehearsal) {
      const nextPossible = dayjs(this.lastRehearsal.nextPossibleRehearsal);
      if (nextPossible.isAfter(possibleStart)) {
        possibleStart = nextPossible;
      }
    }

    return possibleStart;
  }

  private getRehearsalDuration(): number {
    return this.store.selectSnapshot(SettingsState.settingByKey(environment.settingsKeys.rehearsalDuration)).hour;
  }

  private async evaluateRehearsalStatus(): Promise<void> {
    if (this.lastRehearsal) {
      if (
        this.lastRehearsal.nextPossibleRehearsal &&
        dayjs(this.lastRehearsal.nextPossibleRehearsal).isBefore(dayjs())
      ) {
        this.updateRehearsalState(RehearsalStatusEnum.Possible);
        this.rehearsalFinished = false;
      } else if (
        this.lastRehearsal.endsAt &&
        dayjs(this.lastRehearsal.startAt).isBefore(dayjs()) &&
        dayjs(this.lastRehearsal.endsAt).isAfter(dayjs())
      ) {
        this.updateRehearsalState(RehearsalStatusEnum.InProgress);
      } else {
        if (!this.rehearsalFinished) {
          this.updateRehearsalState(RehearsalStatusEnum.Done);
          this.store.dispatch(
            new BandStateActions.SetBand(
              await lastValueFrom(this.apiMyBandService.bandControllerGetMyBand()),
            ),
          );
          this.rehearsalFinished = true;
        }
      }
    }
  }

  private updateRehearsalState(status: RehearsalStatusEnum): void {
    this.store.dispatch(new BandStateActions.SetRehearsalStatus(status));
  }
}
